import { useState, useMemo, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown,faChevronLeft, faChevronRight  } from '@fortawesome/free-solid-svg-icons';
import StaffCard from './StaffCard';
import moment from 'moment';

const StaffGroup = ({
  title,
  staff,
  onUpdate,
  onEdit,
  isArchived = false,
  defaultOpen = false,
  pageSize = 15,
}) => {
  const [open, setOpen] = useState(defaultOpen)
  const [page, setPage] = useState(0)
  const [pageInput, setPageInput] = useState('1');

  useEffect(() => {
    setPage(0)
  }, [staff])

  const total = staff.length
  const totalPages = Math.max(1, Math.ceil(total / pageSize))
  const start = page * pageSize
  const end = Math.min(start + pageSize, total)
  const pageItems = useMemo(() => staff.slice(start, end), [staff, start, end])

  useEffect(() => {
    setPageInput(String(Math.min(page + 1, totalPages)));
  }, [page, totalPages])

  const hasGroupDrafts = useMemo(
    () => staff.some(m => m.policy_staff_status === 'draft'),
    [staff]
  )

  const isExpired = (expiry) => {
    if (!expiry) return false;
    const d = moment(expiry, ['YYYY-MM-DD', 'DD/MM/YYYY'], true);
    return d.isValid() && !d.isAfter(moment().startOf('day'));
  }
  const expiredCount = useMemo(
    () => staff.filter(m => isExpired(m.expiry) || m.active === 0 || m.deletedFlag).length,
    [staff]
  )

  const panelId = `${title.replace(/\s+/g, '-').toLowerCase()}-panel`
  
  const goPrev = () => setPage(p => Math.max(0, p - 1))
  const goNext = () => setPage(p => Math.min(totalPages - 1, p + 1))

  const clampToRange = (n) => {
    if (Number.isNaN(n) || n < 1) return 1
    if (n > totalPages) return totalPages
    return n
  }

  const applyGoToPage = () => {
    const desired = clampToRange(parseInt(pageInput, 10))
    setPage(desired - 1)
    setPageInput(String(desired))
  }

  const onPageInputChange = (e) => {
    const raw = e.target.value.replace(/[^\d]/g, '')
    setPageInput(raw === '' ? '' : String(parseInt(raw, 10)))
  }

  const onPageInputKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      applyGoToPage()
    }
  }

  const onPageInputBlur = () => {
    if (pageInput === '') {
      setPageInput(String(page + 1))
      return
    }
    applyGoToPage()
  }

  return (
    <section className="">
      {/* Header */}
      <button
        type="button"
        className="w-full flex items-center justify-between px-4 py-3 text-left border rounded-md bg-white"
        onClick={() => setOpen(o => !o)}
        aria-expanded={open}
        aria-controls={panelId}
      >
        <div className="flex items-center gap-3">
          <span className="text-lg font-semibold">{title}</span>
          <span className="text-xs px-2 py-0.5 rounded-full border border-gray-300">
            {staff.length}
          </span>
          {hasGroupDrafts && (
            <div className="text-[11px] px-1.5 py-0.5 rounded bg-orange-50 text-orange-700 border border-orange-200" aria-label="Has drafts" >Unsaved Changes</div>
          )}
          {expiredCount > 0 && !isArchived && (
            <span className="text-[11px] px-1.5 py-0.5 rounded bg-red-50 text-red-700 border border-red-200">
              {expiredCount} expired
            </span>
          )}
        </div>

        <FontAwesomeIcon
          icon={faChevronDown}
          className={`transition-transform ${open ? 'rotate-180' : ''}`}
        />
      </button>

      {/* Card */}
      <div
        id={panelId}
        className={`overflow-hidden transition-[max-height] duration-300 ease-in-out ${open ? 'max-h-[2000px]' : 'max-h-0'}`}
      >
        <div className="grid grid-cols-3 gap-4 py-4">
          {pageItems.map(member => (
            <StaffCard
              key={member.id}
              staff={member}
              onUpdate={onUpdate}
              onEdit={onEdit}
              isArchived={isArchived}
            />
          ))}
        </div>
        
        {/* Pagination controls */}
        {totalPages > 1 && (
          <div className="mt-4 flex items-center justify-between">
            <div className="text-sm text-gray-600">
              Showing <span className="font-medium">{total === 0 ? 0 : start + 1}</span>–
              <span className="font-medium">{end}</span> of <span className="font-medium">{total}</span>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={goPrev}
                disabled={page === 0}
                className={`px-3 py-1.5 border rounded disabled:opacity-50 disabled:cursor-not-allowed`}
              >
                <FontAwesomeIcon icon={faChevronLeft} />
              </button>
              <div className="flex items-center gap-2">
                <span className="text-sm">Page</span>
                <input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  value={pageInput}
                  onChange={onPageInputChange}
                  onKeyDown={onPageInputKeyDown}
                  onBlur={onPageInputBlur}
                  className="w-16 px-2 py-1.5 border rounded text-sm text-center"
                  aria-label="Go to page"
                />
                <span className="text-sm">/ {totalPages}</span>
                <button
                  type="button"
                  onClick={applyGoToPage}
                  className="px-2 py-1.5 border rounded text-sm"
                  aria-label="Go to page number"
                >
                  Go
                </button>
              </div>
              <button
                onClick={goNext}
                disabled={page >= totalPages - 1}
                className={`px-3 py-1.5 border rounded disabled:opacity-50 disabled:cursor-not-allowed`}
              >
                <FontAwesomeIcon icon={faChevronRight} />
              </button>
            </div>
          </div>
        )}
      </div>
    </section>
  )
}

export default StaffGroup;
